import type { Job, JobListResponse } from "../types/job";
import { API } from "./base.service";
import { api } from "./config/axios";

/**
 * JobAPI service for interacting with async job endpoints
 *
 * Provides methods to:
 * - Poll job status by ID
 * - List all jobs (paginated)
 * - Get active jobs
 * - Get recent jobs
 *
 * These endpoints support the async job contract where heavy operations
 * return 202 Accepted with a job_id, and clients poll for completion.
 */
class _JobAPI extends API {
  /**
   * Get a single job by ID
   * @param jobId - The job ID to fetch
   * @returns Promise with job data including status, progress, and results
   */
  getJob = async <TDetailedResult = any, TResult = any>(
    jobId: string
  ): Promise<{ data: Job<TDetailedResult, TResult> }> => {
    const response = await api.request({
      url: `/jobs/${jobId}/`,
      method: "GET",
    });

    return response;
  };

  /**
   * Get all jobs for the authenticated user (paginated)
   * @param params - Query parameters (limit, offset, etc.)
   * @returns Promise with paginated job list
   */
  getAllJobs = async <TDetailedResult = any, TResult = any>(
    params?: any
  ): Promise<{ data: JobListResponse<TDetailedResult, TResult> }> => {
    const response = await api.request({
      url: `/jobs/`,
      method: "GET",
      params: params,
    });

    return response;
  };

  /**
   * Get all active (PENDING or STARTED) jobs for the authenticated user
   * @returns Promise with array of active jobs
   */
  getActiveJobs = async <TDetailedResult = any, TResult = any>(): Promise<{
    data: Job<TDetailedResult, TResult>[];
  }> => {
    const response = await api.request({
      url: `/jobs/active/`,
      method: "GET",
    });

    return response;
  };

  /**
   * Get recent jobs for the authenticated user
   * @param limit - Optional limit for number of recent jobs (default: backend default)
   * @returns Promise with array of recent jobs
   */
  getRecentJobs = async <TDetailedResult = any, TResult = any>(
    limit?: number
  ): Promise<{ data: Job<TDetailedResult, TResult>[] }> => {
    const response = await api.request({
      url: `/jobs/recent/`,
      method: "GET",
      params: limit ? { limit } : undefined,
    });

    return response;
  };

  /**
   * Get a single job with ingestion metrics
   * Used for staged transaction workflows to get job metadata + ingestion status
   * @param jobId - The job ID to fetch
   * @returns Promise with job data including ingestion metrics
   */
  getJobWithIngestion = async <TDetailedResult = any, TResult = any>(
    jobId: string
  ): Promise<{
    data: import("../types/job").JobWithIngestion<TDetailedResult, TResult>;
  }> => {
    const response = await api.request({
      url: `/jobs/${jobId}/`,
      method: "GET",
    });

    return response;
  };
}

export const JobAPI = new _JobAPI("jobs");
